// Minimal "Log illness" quick-entry modal, launched from ActiveIllnessWidget
// (ILLNESS_EPISODE_TRACKING_PLAN.md, D-IE-8). Starts a new acute bout without
// going through chat: name + onset date + symptoms + contagious + severity.
// Writes one episodic illness event directly via saveBatchEvents (no Bedrock).

import { useState } from "react";
import { ILLNESS_SYNONYMS, ILLNESS_SEVERITIES } from "@medassist/shared";
import type { IllnessSeverity } from "@medassist/shared";
import { saveBatchEvents, ApiError, type BatchSaveRef } from "../../../lib/api.js";
import { ModalShell } from "../../ModalShell.js";
import { Select } from "../../Select.js";
import { VirusIcon } from "../../icons.js";
import { Toggle } from "../../Toggle.js";

function todayStr(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

/** Canonical acute-illness names for the datalist: every synonym target plus a
 *  few common ones the synonym map doesn't need an alias for. */
const ILLNESS_NAME_SUGGESTIONS = [
  ...new Set([
    ...Object.values(ILLNESS_SYNONYMS),
    "bronchitis",
    "ear infection",
    "food poisoning",
    "strep throat",
    "covid",
  ]),
].sort();

const inputCls =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100";

export function QuickIllnessLogModal({
  patientId,
  onClose,
  onLogged,
}: {
  patientId: string;
  onClose: () => void;
  /** Called with the saved event ref + a toast label so the parent can show the undo toast. */
  onLogged: (ref: BatchSaveRef, payload: Record<string, unknown>, label: string) => void;
}) {
  const [name, setName] = useState("");
  const [onsetDate, setOnsetDate] = useState(todayStr);
  const [symptomsText, setSymptomsText] = useState("");
  const [contagious, setContagious] = useState(false);
  const [severity, setSeverity] = useState<IllnessSeverity | "">("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter an illness name.");
      return;
    }
    setSaving(true);
    setError(null);
    const symptoms = symptomsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload: Record<string, unknown> = {
      name: trimmed,
      status: "active",
      episodic: true,
      onsetDate,
      diagnosedOn: todayStr(),
      ...(symptoms.length ? { symptoms } : {}),
      ...(contagious ? { contagious: true } : {}),
      ...(severity ? { severity } : {}),
    };
    try {
      const [ref] = await saveBatchEvents(patientId, [{ eventType: "illness", payload }]);
      onLogged(ref, payload, `Logged ${trimmed}`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't log illness");
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title="Log illness"
      icon={<VirusIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => void save()}
            disabled={saving}
            className="rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-60"
          >
            {saving ? "Saving…" : "Log it"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
        )}

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Illness</span>
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            list="illness-name-suggestions"
            placeholder="e.g. cold, flu, stomach bug"
            className={inputCls}
          />
          <datalist id="illness-name-suggestions">
            {ILLNESS_NAME_SUGGESTIONS.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Started on</span>
          <input
            type="date"
            value={onsetDate}
            max={todayStr()}
            onChange={(e) => setOnsetDate(e.target.value)}
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
            Symptoms <span className="font-normal text-gray-400">(comma-separated, optional)</span>
          </span>
          <input
            type="text"
            value={symptomsText}
            onChange={(e) => setSymptomsText(e.target.value)}
            placeholder="e.g. cough, sore throat, fever"
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
            Severity <span className="font-normal text-gray-400">(optional)</span>
          </span>
          <Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as IllnessSeverity | "")}
            className="w-full"
          >
            <option value="">Not sure</option>
            {ILLNESS_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex cursor-pointer items-center gap-3 pt-1">
          <Toggle checked={contagious} onChange={setContagious} />
          <span className="text-sm text-gray-700 dark:text-gray-300">Contagious</span>
        </label>
      </div>
    </ModalShell>
  );
}
